import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import type { GameProgressResponse } from "@models/game";

const DOMAIN_KEYS = [
  "memory",
  "attention",
  "processing_speed",
  "language",
  "cognitive_flexibility",
  "spatial",
] as const;

const SIZE = 220;
const CENTER = SIZE / 2;
const LABEL_R = 0.95;
const MAX_SCORE = 100;

/** Hexagonal radar chart: 220×220px, 6 domains, baseline at 50%, tap → progress */
export interface DomainRadarMiniChartProps {
  progress: GameProgressResponse | null | undefined;
  onTap?: () => void;
}

const DomainRadarMiniChart: FC<DomainRadarMiniChartProps> = ({
  progress,
  onTap,
}) => {
  const { t } = useTranslation();

  const { points, baselinePoints, labels } = useMemo(() => {
    const angleStep = (2 * Math.PI) / 6;
    const radius = CENTER * 0.80;

    const toPoint = (r: number, i: number) => {
      const angle = -Math.PI / 2 + i * angleStep;
      return {
        x: CENTER + r * Math.cos(angle),
        y: CENTER + r * Math.sin(angle),
      };
    };

    const domainValues = DOMAIN_KEYS.map(
      (key) => (progress?.domains?.[key] ?? 0) / MAX_SCORE,
    );
    const points = domainValues.map((v, i) =>
      toPoint(radius * Math.min(1, Math.max(0, v)), i),
    );
    const baselinePoints = DOMAIN_KEYS.map((_, i) =>
      toPoint(radius * 0.5, i),
    );
    const labelPoints = DOMAIN_KEYS.map((_, i) =>
      toPoint(radius * LABEL_R, i),
    );

    const labels = DOMAIN_KEYS.map((key, i) => ({
      key,
      text: t(`games.sessionSummary.domains.${key}`),
      ...labelPoints[i],
    }));

    return {
      points,
      baselinePoints,
      labels,
    };
  }, [progress?.domains, t]);

  const hasData =
    progress?.domains &&
    DOMAIN_KEYS.some((k) => (progress.domains[k] ?? 0) > 0);

  const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(" ");
  const baselinePolygon = baselinePoints.map((p) => `${p.x},${p.y}`).join(" ");

  if (!hasData) {
    return (
      <Box
        onClick={onTap}
        sx={{
          width: SIZE,
          height: SIZE,
          mx: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          bgcolor: "action.hover",
          cursor: onTap ? "pointer" : undefined,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "center", px: 1, fontSize: 13 }}
        >
          {t("games.cta.widgets.radarEmpty")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={onTap}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        cursor: onTap ? "pointer" : undefined,
      }}
      aria-label={t("games.cta.widgets.radarAria")}
    >
      <svg width={SIZE} height={SIZE} style={{ overflow: "visible" }}>
        <polygon
          points={baselinePolygon}
          fill="none"
          stroke="rgba(0,0,0,0.12)"
          strokeWidth={1}
        />
        <polygon
          points={polygonPoints}
          fill="rgba(25, 118, 210, 0.4)"
          stroke="rgb(25, 118, 210)"
          strokeWidth={1.5}
        />
        {labels.map((l) => (
          <text
            key={l.key}
            x={l.x}
            y={l.y}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: 13,
              fill: "currentColor",
              opacity: 0.8,
            }}
          >
            {l.text.length > 10 ? l.text.split(" ").map((word, index) => <tspan key={index} x={l.x} y={l.y + index * 15}>{word}</tspan>) : (<>{l.text}</>)}
          </text>
        ))}
      </svg>
    </Box>
  );
};

export default DomainRadarMiniChart;
